import React, { useState, useEffect } from "react";

const API_URL = "https://api.yogavantha.com";
const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const todayIndex = new Date().getDay();

const AttendanceManagementPage = () => {
  // STATE
  const [branches, setBranches] = useState([]);
  const [batches, setBatches] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [attendanceData, setAttendanceData] = useState({}); // { memberId: {dayIdx: bool} }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // FETCH: Branches on mount
  useEffect(() => {
    fetch(`${API_URL}/branches/`)
      .then((res) => res.json())
      .then(setBranches)
      .catch(console.error);
  }, []);

  // FETCH: Batches when branch changes
  useEffect(() => {
    if (!selectedBranch) {
      setBatches([]);
      setSelectedBatch("");
      return;
    }
    fetch(`${API_URL}/batches/?branch_id=${selectedBranch}`)
      .then((res) => res.json())
      .then(setBatches)
      .catch(console.error);
  }, [selectedBranch]);

  // FETCH: Members when batch changes
  useEffect(() => {
    if (!selectedBranch || !selectedBatch) {
      setMembers([]);
      setAttendanceData({});
      return;
    }
    setLoading(true);
    // Find code for branch (may not be needed, adapt if API needs)
    const branchObj = branches.find(
      (b) => String(b.id) === String(selectedBranch)
    );
    const branchCode = branchObj?.code || "";
    fetch(
      `${API_URL}/members/?branch_code=${branchCode}&batch_id=${selectedBatch}`
    )
      .then((res) => res.json())
      .then((data) => {
        setMembers(data);
        // Initialize attendance data: unchecked for all days
        const att = {};
        data.forEach((member) => {
          att[member.id] = {};
          daysOfWeek.forEach((_, i) => (att[member.id][i] = false));
        });
        setAttendanceData(att);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedBranch, selectedBatch, branches]);

  // HANDLER: Checkbox
  const handleAttendanceChange = (memberId, dayIdx, checked) => {
    setAttendanceData((prev) => ({
      ...prev,
      [memberId]: { ...prev[memberId], [dayIdx]: checked },
    }));
  };

  // HANDLER: Save Attendance
  const saveAttendance = async () => {
    setSaving(true);
    let successCount = 0;
    let failCount = 0;

    const apiCalls = [];

    for (const memberId in attendanceData) {
      const daysAttendance = attendanceData[memberId];

      // Assume date is week start (e.g. Sunday of current week)
      const today = new Date();
      const dayOfWeek = today.getDay();
      const sunday = new Date(today);
      sunday.setDate(today.getDate() - dayOfWeek); // go back to Sunday
      sunday.setUTCHours(0, 0, 0, 0);
      const weekStartDateISO = sunday.toISOString();

      const record = {
        member_id: memberId,
        batch_id: parseInt(selectedBatch),
        date: weekStartDateISO, // week start date for this attendance record
        sunday: !!daysAttendance[0],
        monday: !!daysAttendance[1],
        tuesday: !!daysAttendance[2],
        wednesday: !!daysAttendance[3],
        thursday: !!daysAttendance[4],
        friday: !!daysAttendance[5],
        saturday: !!daysAttendance[6],
        created_by: "admin",
      };

      apiCalls.push(
        fetch(`${API_URL}/attendances/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(record),
        })
          // eslint-disable-next-line no-loop-func
          .then((r) => (r.ok ? successCount++ : failCount++))
          // eslint-disable-next-line no-loop-func
          .catch(() => failCount++)
      );
    }

    await Promise.all(apiCalls);
    setSaving(false);

    if (failCount === 0) {
      alert("Attendance saved successfully!");
    } else {
      alert(
        `Saved ${successCount}, Failed ${failCount} rows. Check backend/API errors.`
      );
    }
  };

  // RENDER
  return (
    <div className="p-8 bg-white rounded-lg shadow-xl max-w-6xl mx-auto">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">
        Attendance Management
      </h2>
      {/* Branch/Batch selectors */}
      <div className="mb-6 flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4">
        <div className="w-full md:w-1/3">
          <label
            className="block text-gray-700 text-sm font-semibold mb-2"
            htmlFor="branchSelect"
          >
            Select Branch:
          </label>
          <select
            id="branchSelect"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedBranch}
            onChange={(e) => {
              setSelectedBranch(e.target.value);
              setSelectedBatch("");
            }}
          >
            <option value="">-- Select Branch --</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full md:w-1/3">
          <label
            className="block text-gray-700 text-sm font-semibold mb-2"
            htmlFor="batchSelect"
          >
            Select Batch:
          </label>
          <select
            id="batchSelect"
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedBatch}
            onChange={(e) => setSelectedBatch(e.target.value)}
            disabled={!selectedBranch}
          >
            <option value="">-- Select Batch --</option>
            {batches.map((batch) => (
              <option key={batch.id} value={batch.id}>
                {batch.name}
              </option>
            ))}
          </select>
        </div>
        <button
          className="bg-blue-600 text-white py-2 px-5 rounded-md hover:bg-blue-700 font-semibold w-full md:w-auto mt-auto"
          disabled={loading || !(selectedBranch && selectedBatch)}
        >
          {loading ? "Loading..." : "Load Members"}
        </button>
      </div>
      {/* Attendance Table */}
      {members.length > 0 && (
        <div className="overflow-x-auto mt-8">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  S.No.
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Member ID
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Photo
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Name
                </th>
                {daysOfWeek.map((day, idx) => (
                  <th
                    key={day}
                    className={`py-3 px-4 text-center text-xs font-medium text-gray-600 uppercase tracking-wider ${
                      todayIndex === idx ? "bg-yellow-200 text-yellow-800" : ""
                    }`}
                  >
                    {day[0]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {members.map((member, serialNo) => (
                <tr key={member.id}>
                  <td className="py-3 px-4">{serialNo + 1}</td>
                  <td className="py-3 px-4">{member.id}</td>
                  <td className="py-3 px-4">
                    <img
                      src={
                        member.photo ||
                        "https://ui-avatars.com/api/?name=" +
                          encodeURIComponent(member.full_name || "U N")
                      }
                      alt={member.full_name}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  </td>
                  <td className="py-3 px-4">{member.full_name}</td>
                  {daysOfWeek.map((_, dayIdx) => (
                    <td
                      key={dayIdx}
                      className={`py-3 px-4 text-center ${
                        todayIndex === dayIdx ? "bg-yellow-100" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={attendanceData[member.id]?.[dayIdx] || false}
                        onChange={(e) =>
                          handleAttendanceChange(
                            member.id,
                            dayIdx,
                            e.target.checked
                          )
                        }
                        className="form-checkbox h-5 w-5 text-purple-600 rounded"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-6 text-right">
            <button
              className="bg-green-600 text-white py-2 px-6 rounded-md hover:bg-green-700 font-semibold shadow-md"
              onClick={saveAttendance}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Attendance"}
            </button>
          </div>
        </div>
      )}
      {/* Empty states */}
      {members.length === 0 && selectedBranch && selectedBatch && !loading && (
        <p className="mt-8 text-center text-gray-600">
          No members found for the selected branch and batch.
        </p>
      )}
      {(!selectedBranch || !selectedBatch) && (
        <p className="mt-8 text-center text-gray-600">
          Please select a Branch and a Batch to load members.
        </p>
      )}
    </div>
  );
};

export default AttendanceManagementPage;
